import { Component, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Button } from 'ustore-ui';

import { CustomInputComponent } from '../../shared/components/custom-input/custom-input.component';
import { IndicationService, IndicationRequest } from '../../core/services/indication.service';
import { ToastService } from '../../shared/services/toast.service';
import { CustomValidators } from '../../shared/validators';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { I18nService } from '../../core/services/i18n.service';
import { formatCnpj, unformatCnpj } from '../../core/utils/cnpj.util';
// Interface para a resposta da API do IBGE
interface IBGECity {
  id: number;
  nome: string;
  microrregiao?: any;
  'regiao-imediata'?: any;
}

@Component({
  selector: 'umkt-indication-pme',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    Button,
    CustomInputComponent,
    I18nPipe
],
  templateUrl: './indication-pme.component.html',
  styleUrls: ['./indication-pme.component.scss'],
})
export class IndicationPmeComponent implements OnDestroy {
  indicationPmeForm: FormGroup;
  isSubmitting = false;
  
  // Subject para gerenciar subscriptions
  private destroy$ = new Subject<void>();
  private formBuilder = inject(FormBuilder);
  private indicationService = inject(IndicationService);
  private toastService = inject(ToastService);
  private http = inject(HttpClient);
  private i18nService = inject(I18nService);

  constructor() {
    this.indicationPmeForm = this.formBuilder.group({
      contactName: ['', [Validators.required, CustomValidators.noWhitespaceOnly, Validators.maxLength(255)]],
      companyName: ['', [Validators.required, CustomValidators.noWhitespaceOnly, Validators.maxLength(255)]],
      companyCNPJ: ['', [Validators.required, CustomValidators.cnpj]],
      companyEmail: ['', [Validators.required, Validators.email, CustomValidators.emailNoSpaces]],
      companyPhone: ['', [Validators.required, CustomValidators.phone]]
    });
  }

  onSubmit(event?: Event): void {
    this.markFormGroupTouched();
    
    const formElement = (event?.target as HTMLFormElement);
    const formValue = this.indicationPmeForm.value;
    const requiredFields = [
      'contactName', 'companyName', 'companyCNPJ', 'companyEmail', 'companyPhone'
    ];
    
    const hasEmptyFields = requiredFields.some(field => !formValue[field]);
    
    if (hasEmptyFields && formElement) {
      setTimeout(() => {
        const firstEmptyField = this.findFirstEmptyRequiredField(formElement, requiredFields);
        if (firstEmptyField) {
          firstEmptyField.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
          
          setTimeout(() => {
            firstEmptyField.focus();
          }, 300);
        }
        
        formElement.reportValidity();
      }, 100);
      return;
    }

    if (formElement && !formElement.checkValidity()) {
      setTimeout(() => {
        const firstInvalidField = formElement.querySelector(':invalid') as HTMLElement;
        if (firstInvalidField) {
          firstInvalidField.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });

          setTimeout(() => {
            firstInvalidField.focus();
          }, 300);
        }
        
        formElement.reportValidity();
      }, 100);
      return;
    }

    if (!this.isSubmitting) {
      this.performSubmitWithCityValidation();
    }
  }

  private async performSubmitWithCityValidation(): Promise<void> {
    if (this.isSubmitting) return;

    this.indicationPmeForm.markAllAsTouched();
    if (this.indicationPmeForm.invalid) {
      this.toastService.showError(
        this.i18nService.instant('pages.indicationPme.messages.formError'), 
        3000, 
        'top-center'
      );
      return;
    }

    this.isSubmitting = true;

    try {
      // Mapear dados do formulário para o formato da API
      const indicationData = this.mapFormDataToEndpoint();
      this.submitToEndpoint(indicationData);
      
    } catch (error) {
      console.error('Erro na validação da cidade:', error);
      this.isSubmitting = false;
      this.toastService.showError(
        this.i18nService.instant('pages.indicationPme.messages.validationError'),
        5000,
        'top-center'
      );
    }
  }



  private removePhoneMask(phone: string): string {
    return phone ? phone.replace(/\D/g, '') : '';
  }

  private removeCNPJMask(cnpj: string): string {
    return unformatCnpj(cnpj);
  }

  private mapFormDataToEndpoint(): IndicationRequest {
    const formValue = this.indicationPmeForm.value;
    
    return {
      employee: {
        name: 'Marketplace',
        phone: '11998888889',
        sector: 'Marketplace',
        email: 'admin.hub@usto.re',
        city: 'São Paulo',
        state: 'SP'
      },
      indication: {
        name: formValue.contactName,
        companyName: formValue.companyName,
        document: this.removeCNPJMask(formValue.companyCNPJ),
        phone: this.removePhoneMask(formValue.companyPhone), 
        email: formValue.companyEmail
      },
      complementaryData: {
        bestContactTime: '',
        contactMethod: '',
        additionalInfo: '',
        companyAware: true
      }
    };
  }

  private submitToEndpoint(data: IndicationRequest): void {
    this.indicationService.submitIndication(data).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        if (response.status === 'success') {
          this.toastService.showSuccess(
            this.i18nService.instant('pages.indicationPme.messages.success'),
            3000,
            'top-center'
          );

          this.isSubmitting = false;
          this.resetForm();
        } else {
          const errorMessage = response.msg || this.i18nService.instant('pages.indicationPme.messages.processError');
          this.toastService.showError(errorMessage, 5000, 'top-center');
          this.isSubmitting = false;
        }
      },
      error: (error) => {
        console.error('Erro ao enviar dados da empresa:', error);

        let errorMessage = this.i18nService.instant('pages.indicationPme.messages.connectionError');

        if (error.status === 400) {
          errorMessage = this.i18nService.instant('pages.indicationPme.messages.invalidData');
        } else if (error.error && error.error.msg) {
          errorMessage = error.error.msg;
        }

        this.toastService.showError(errorMessage, 5000, 'top-center');
        this.isSubmitting = false;
      }
    });
  }

  onReset(): void {
    this.resetForm();
  }

  private resetForm(): void {
    this.indicationPmeForm.reset();
  }

  private findFirstEmptyRequiredField(formElement: HTMLFormElement, requiredFields: string[]): HTMLElement | null {
    for (const fieldName of requiredFields) {
      const fieldElement = formElement.querySelector(`#${fieldName}`) as HTMLElement;
      if (fieldElement && !this.indicationPmeForm.get(fieldName)?.value) {
        return fieldElement;
      }
    }
    return null;
  }

  private markFormGroupTouched(): void {
    Object.keys(this.indicationPmeForm.controls).forEach(key => {
      const control = this.indicationPmeForm.get(key);
      control?.markAsTouched();
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.indicationPmeForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.indicationPmeForm.get(fieldName);
    if (field && field.errors && field.touched) {
      if (field.errors['required']) {
        return 'Esse campo é obrigatório';
      }
      if (field.errors['email']) {
        return 'Por favor, insira um endereço de email válido';
      }
      if (field.errors['emailWithSpaces']) {
        return 'O email não pode conter espaços';
      }
      if (field.errors['invalidCorporateEmail']) {
        return 'Email corporativo inválido';
      }
      if (field.errors['invalidCity']) {
        return 'Cidade não encontrada para o estado selecionado';
      }
      
      if (field.errors['invalidPhone']) {
        return 'Telefone inválido. Digite apenas os números';
      }
      if (field.errors['whitespaceOnly']) {
        return 'Este campo não pode conter apenas espaços';
      }
    }
    return '';
  }

  get contactNameControl() {
    return this.indicationPmeForm.get('contactName');
  }

  get companyNameControl() {
    return this.indicationPmeForm.get('companyName');
  }

  get companyCNPJControl() {
    return this.indicationPmeForm.get('companyCNPJ');
  }

  get companyEmailControl() {
    return this.indicationPmeForm.get('companyEmail');
  }

  get companyPhoneControl() {
    return this.indicationPmeForm.get('companyPhone');
  }

  // Aplicar máscara de telefone 
  applyPhoneMask(event: any): void {
    const input = event.target;
    const cursorPosition = input.selectionStart;
    const inputType = event.inputType;

    let value = input.value.replace(/\D/g, '');
    
    if (value.length > 11) {
      value = value.substring(0, 11);
    }
    
    let formattedValue = '';
    
    if (value.length >= 2) {
      const ddd = value.substring(0, 2);
      const phoneNumber = value.substring(2);
      
      if (phoneNumber.length === 0) {
        formattedValue = `(${ddd}`;
      } else if (phoneNumber.length <= 4) {
        formattedValue = `(${ddd}) ${phoneNumber}`;
      } else if (phoneNumber.length === 5) {
        formattedValue = `(${ddd}) ${phoneNumber}`;
      } else if (phoneNumber.length <= 8) {
        const firstPart = phoneNumber.substring(0, 4);
        const secondPart = phoneNumber.substring(4);
        formattedValue = `(${ddd}) ${firstPart}-${secondPart}`;
      } else {
        const firstPart = phoneNumber.substring(0, 5);
        const secondPart = phoneNumber.substring(5);
        formattedValue = `(${ddd}) ${firstPart}-${secondPart}`;
      }
    } else if (value.length === 1) {
      formattedValue = `(${value}`;
    } else {
      formattedValue = '';
    }
    
    input.value = formattedValue;
    
    const controlName = input.getAttribute('formControlName');
    if (controlName) {
      this.indicationPmeForm.get(controlName)?.setValue(formattedValue);
    }
    
    if (inputType === 'deleteContentBackward' || inputType === 'deleteContentForward') {
      let newCursorPosition = cursorPosition;
      
      if (newCursorPosition > 0 && newCursorPosition <= formattedValue.length) {
        const charAtCursor = formattedValue.charAt(newCursorPosition - 1);
        if (charAtCursor === '(' || charAtCursor === ')' || charAtCursor === ' ' || charAtCursor === '-') {
          newCursorPosition = Math.max(0, newCursorPosition - 1);
        }
      }
      
      setTimeout(() => {
        input.setSelectionRange(newCursorPosition, newCursorPosition);
      }, 0);
    }
  }

  onPhoneKeydown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;
    const value = input.value;

    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End', 'Insert'
    ];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    if (event.ctrlKey && ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase())) {
      return;
    }

    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length >= 11) {
      event.preventDefault();
      return;
    }
  }

  // Aplicar máscara de CNPJ (numérico ou alfanumérico)
  applyCNPJMask(event: any): void {
    const input = event.target;
    if (event.inputType === 'deleteContentBackward') {
      return;
    }
    const originalCursorPos = input.selectionStart;
    const value = formatCnpj(input.value);
    input.value = value;
    const newCursorPos = Math.min(originalCursorPos, value.length);
    setTimeout(() => {
      input.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);

    const controlName = input.getAttribute('formControlName');
    if (controlName) {
      this.indicationPmeForm.get(controlName)?.setValue(value);
    }
  }

  onClick(event: any): void {
    const formElement = document.querySelector('.form-section');
    if (formElement) {
      formElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }

  onRegulamentClick(event: any): void {
    window.open('https://assets-mktplace-001.s3.sa-east-1.amazonaws.com/Regulamento-A%C3%A7%C3%A3o-Indica%C3%A7ao-PME.pdf', '_blank');
  }



  /** Não deixar escrever espaço no primeiro caractere */
  preventLeadingSpace(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (event.key === ' ' && input.selectionStart === 0) {
      event.preventDefault(); // 🚫 não deixa digitar espaço no começo
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}